import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { SupportedLanguages, getLocale } from '@/locales';
import { COOKIE_PATH, computeCookieDomain } from '@/pages/Login/Login';
import { getLanguage, setLanguage } from '@/redux/app';
import { getBrand } from '@/redux/brand';
import { getUser } from '@/redux/user';
import { UserRoles } from '@/services/innovorder/user/user.type';
import frMessages from '@/locales/fr';
import enMessages from '@/locales/en';
import itMessages from '@/locales/it';
import sodexoMessages from '@/locales/sodexo';
import esMessages from '@/locales/es';
import zhCNMessages from '@/locales/zh-CN';
import zhTWMessages from '@/locales/zh-TW';
import arMessages from '@/locales/ar';
import deMessages from '@/locales/de';

const messagesMap = {
    fr: frMessages,
    en: enMessages,
    it: itMessages,
    sodexo: sodexoMessages,
    es: esMessages,
    'zh-CN': zhCNMessages,
    'zh-TW': zhTWMessages,
    ar: arMessages,
    de: deMessages,
};

const locales = {
    fr: import('dayjs/locale/fr'),
    en: import('dayjs/locale/en'),
    it: import('dayjs/locale/it'),
    sodexo: import('dayjs/locale/fr'),
    es: import('dayjs/locale/es'),
    'zh-CN': import('dayjs/locale/zh-cn'),
    'zh-TW': import('dayjs/locale/zh-tw'),
    ar: import('dayjs/locale/ar'),
    de: import('dayjs/locale/de'),
};

const RTL_LANGUAGES: ReadonlySet<string> = new Set([SupportedLanguages.ar]);

const htmlLangMap: Record<SupportedLanguages, string> = {
    [SupportedLanguages.fr]: 'fr',
    [SupportedLanguages.en]: 'en',
    [SupportedLanguages.it]: 'it',
    [SupportedLanguages.sodexo]: 'fr',
    [SupportedLanguages.es]: 'es',
    [SupportedLanguages['zh-CN']]: 'zh-CN',
    [SupportedLanguages['zh-TW']]: 'zh-TW',
    [SupportedLanguages.ar]: 'ar',
    [SupportedLanguages.de]: 'de',
};

const setDayJsLocale = (language: keyof typeof locales) => {
    locales[language].then(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        dayjs.locale({ ...language, weekStart: 1 });
    });
};

const setDocumentDirection = (language: string) => {
    if (typeof document === 'undefined') return;
    document.documentElement.dir = RTL_LANGUAGES.has(language) ? 'rtl' : 'ltr';
    document.documentElement.lang = htmlLangMap[language as SupportedLanguages] ?? 'fr';
};

export const localeCookieLocation = 'innovorder.NG_TRANSLATE_LANG_KEY';

export const useLocaleHandler = (): {
    currentLocale: string;
    messages: Record<string, string>;
    supportedLanguages: SupportedLanguages[];
} => {
    const dispatch = useDispatch();
    const currentLanguage = useSelector(getLanguage);
    const locale = getLocale();
    const [currentLocale, setCurrentLocale] = useState<string>(locale);
    const [cookies, setCookies] = useCookies();
    const brand = useSelector(getBrand);
    const user = useSelector(getUser);
    const supportedLanguages = useMemo(() => {
        const isSodexoDictionaryEnabled = brand?.options?.ENABLE_SODEXO_DICTIONARY?.isActivated;
        return Object.values(SupportedLanguages)
            .filter(
                (language) =>
                    language !== SupportedLanguages.sodexo ||
                    (language === SupportedLanguages.sodexo &&
                        isSodexoDictionaryEnabled &&
                        user?.role !== UserRoles.admin),
            )
            .map((language) => language);
    }, [brand?.options, user?.role]);
    const localeCookie = cookies[localeCookieLocation];

    const loadLocale = useCallback(
        async (newLocale: keyof typeof locales) => {
            if (!supportedLanguages.includes(newLocale as SupportedLanguages)) newLocale = SupportedLanguages.fr;
            setCurrentLocale(newLocale);
            setDayJsLocale(newLocale);
            setDocumentDirection(newLocale);
        },
        [supportedLanguages],
    );

    useEffect(() => {
        dispatch(setLanguage(localeCookie || locale));
    }, [dispatch, locale, localeCookie]);

    useEffect(() => {
        if (currentLanguage) {
            loadLocale(currentLanguage);
            setCookies(localeCookieLocation, currentLanguage, {
                domain: computeCookieDomain(window.location.host),
                path: COOKIE_PATH,
            });
        }
    }, [currentLanguage, supportedLanguages, loadLocale, setCookies]);

    return {
        currentLocale,
        messages: messagesMap[currentLocale as keyof typeof messagesMap] || messagesMap.fr,
        supportedLanguages,
    };
};
